import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import useAuthStore from '@/store/authStore';
import useAppStore from '@/store/appStore';
import { BusinessType, Role } from '@/model/entities';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import SubdomainInput from '../input/SubdomainInput';

interface AddRestaurantFormData {
  email: string;
  password: string;
  confirmPassword: string;
  plan: "basic" | "advanced" | "premium";
  restaurantName: string;
  subdomain: string;
  ownerPhoneNumber: string;
  address: string;
  phone: string;
  businessType: string;
  role: string;
}

interface AddRestaurantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const AddRestaurantModal = ({ open, onOpenChange, onSuccess }: AddRestaurantModalProps) => {
  const { t } = useTranslation();
  const { signup } = useAuthStore();
  const { direction } = useAppStore();
  const [currentStep, setCurrentStep] = useState(1);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('input.email'))
      .required(t('input.required')),
    password: Yup.string()
      .min(8, t('input.passwordMinLength', { minLength: 8 }))
      .required(t('input.required')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], t('input.passwordMatch'))
      .required(t('input.required')),
    plan: Yup.string()
      .required(t('input.required')),
    restaurantName: Yup.string()
      .required(t('input.required')),
    subdomain: Yup.string()
      .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, t('input.subdomainInvalid'))
      .required(t('input.required')),
    ownerPhoneNumber: Yup.string()
      .matches(/^[0-9+\-() ]+$/, t('input.phoneInvalid'))
      .required(t('input.required')),
    address: Yup.string()
      .required(t('input.required')),
    phone: Yup.string()
      .matches(/^[0-9+\-() ]+$/, t('input.phoneInvalid'))
      .required(t('input.required')),
    businessType: Yup.string()
      .oneOf(Object.values(BusinessType))
      .required(t('input.required'))
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      plan: 'basic', // Set basic as default
      restaurantName: '',
      subdomain: '',
      ownerPhoneNumber: '',
      address: '',
      phone: '',
      businessType: '',
      role: Role.OWNER
    },
    validationSchema,
    onSubmit: async (values: AddRestaurantFormData) => {
      try {
        await signup(values);
        onOpenChange(false);
        setCurrentStep(1);
        formik.resetForm();
        onSuccess();
        toast.success(t('restaurantAddedSuccess'));
      } catch (error) {
        console.error('Error creating restaurant:', error);
        toast.error(t('restaurantAddedError'));
      }
    }
  });

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">{t('emailAddress')}</Label>
        <Input
          id="email"
          type="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className="text-right"
          placeholder={t('input.emailPlaceholder')}
        />
        {formik.touched.email && formik.errors.email && (
          <div className="text-red-500 text-sm">{formik.errors.email}</div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="ownerPhoneNumber">{t('signup.ownerPhoneNumber')}</Label>
        <Input
          id="ownerPhoneNumber"
          value={formik.values.ownerPhoneNumber}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t('input.ownerPhoneNumberPlaceholder')}
        />
        {formik.touched.ownerPhoneNumber && formik.errors.ownerPhoneNumber && (
          <div className="text-red-500 text-sm">{formik.errors.ownerPhoneNumber}</div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">{t('password')}</Label>
        <Input
          id="password"
          type="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t('input.passwordPlaceholder')}
        />
        {formik.touched.password && formik.errors.password && (
          <div className="text-red-500 text-sm">{formik.errors.password}</div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
        <Input
          id="confirmPassword"
          type="password"
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t('input.confirmPasswordPlaceholder')}
        />
        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
          <div className="text-red-500 text-sm">{formik.errors.confirmPassword}</div>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="plan">{t('selectPlan')}</Label>
        <Select 
          value={formik.values.plan} 
          onValueChange={(value) => formik.setFieldValue('plan', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('input.selectPlanPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="basic" defaultChecked>{t('plans.basic')}</SelectItem>
            <SelectItem value="advanced">{t('plans.advanced')}</SelectItem>
            <SelectItem value="premium">{t('plans.premium')}</SelectItem>
          </SelectContent>
        </Select>
        {formik.touched.plan && formik.errors.plan && (
          <div className="text-red-500 text-sm">{formik.errors.plan}</div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="restaurantName">{t('restaurantName')}</Label>
        <Input
          id="restaurantName"
          value={formik.values.restaurantName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t('input.restaurantNamePlaceholder')}
        />
        {formik.touched.restaurantName && formik.errors.restaurantName && (
          <div className="text-red-500 text-sm">{formik.errors.restaurantName}</div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="subdomain">{t('signup.subdomain')}</Label>
        <SubdomainInput   
          name="subdomain"       
          value={formik.values.subdomain}
          onChange={event => formik.setFieldValue('subdomain', event.target.value)}
          onBlur={formik.handleBlur}
          error={formik.touched.subdomain ? formik.errors.subdomain : undefined}
          placeholder={t('input.subdomainPlaceholder')}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="address">{t('address')}</Label>
        <Input
          id="address"
          value={formik.values.address}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t('input.addressPlaceholder')}
        />
        {formik.touched.address && formik.errors.address && (
          <div className="text-red-500 text-sm">{formik.errors.address}</div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone">{t('phone')}</Label>
        <Input
          id="phone"
          type="tel"
          value={formik.values.phone}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t('input.phonePlaceholder')}
        />
        {formik.touched.phone && formik.errors.phone && (
          <div className="text-red-500 text-sm">{formik.errors.phone}</div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="businessType">{t('businessType')}</Label>
        <Select 
          value={formik.values.businessType} 
          onValueChange={(value) => formik.setFieldValue('businessType', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('input.selectBusinessType')} />
          </SelectTrigger>
          <SelectContent>
            {Object.values(BusinessType).map((type) => (
              <SelectItem key={type} value={type}>
                {t(`businessTypes.${type}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {formik.touched.businessType && formik.errors.businessType && (
          <div className="text-red-500 text-sm">{formik.errors.businessType}</div>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('addRestaurant', 'Add Restaurant')}</DialogTitle>
          <DialogDescription>
            {t('signup.addRestaurantDescription', 'Fill in the details to add a new restaurant')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={formik.handleSubmit}>
          <div className="py-4">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </div>
          <DialogFooter className="flex justify-between">
            {currentStep > 1 && (
              <Button type="button" variant="outline" onClick={handleBack}>
                {direction === 'rtl' ? <ArrowRight className="mr-2 h-4 w-4" /> : <ArrowLeft className="mr-2 h-4 w-4" />}
                {t('buttons.previous')}
              </Button>
            )}
            {currentStep < 3 ? (
              <Button type="button" onClick={handleNext}>
                {t('buttons.next')}
                {direction === 'rtl' ? <ArrowLeft className="ml-2 h-4 w-4" /> : <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            ) : (
              <Button type="submit" disabled={formik.isSubmitting}>
                {t('buttons.submit')}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddRestaurantModal;